import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  useTheme,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { ref, onValue, update, push } from "firebase/database";
import { db } from "../../firebase"; // Adjust based on your Firebase setup
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash, faPlus } from "@fortawesome/free-solid-svg-icons";
import SearchBar from "../Navigation/SearchBar";
import Notification from "../Notification";

const KnowledgeBaseSettings = () => {
  const [knowledgeBase, setKnowledgeBase] = useState([]);
  const [filteredKnowledgeBase, setFilteredKnowledgeBase] = useState([]);
  const theme = useTheme();
  const [searchTerm, setSearchTerm] = useState("");
  const [newCategory, setNewCategory] = useState("");
  const [newQuestion, setNewQuestion] = useState("");
  const [newAnswer, setNewAnswer] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const categories = [
    { name: "Applications", icon: "app-store-ios" },
    { name: "Settings", icon: "cog" },
    { name: "Deployment", icon: "tools" },
    { name: "Notifications", icon: "bell" },
    { name: "User Management", icon: "user-circle" },
    { name: "Monitoring", icon: "question-circle" },
  ];

  useEffect(() => {
    const kbRef = ref(db, "knowledgeBase");
    onValue(kbRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const formattedData = [];

        Object.keys(data).forEach((key) => {
          data[key].items.forEach((item, index) => {
            formattedData.push({
              id: `${key}_${index}`, // Unique ID for each article
              category: data[key].category,
              question: item.question,
              answer: item.answer,
              categoryId: key,
              itemIndex: index,
            });
          });
        });

        setKnowledgeBase(formattedData);
        setFilteredKnowledgeBase(formattedData); // Initially, show all records
      }
    });
  }, []);

  const handleDelete = async (id, categoryId, itemIndex) => {
    // Ask for confirmation before proceeding
    const result = await Notification.confirm({
      theme,
      title: "Confirm Deletion",
      text: "Are you sure you want to delete this article? This action cannot be undone.",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    });
  
    if (result.isConfirmed) {
      // Proceed with deletion if the user confirms
      const updatedItems = knowledgeBase
        .filter((item) => item.categoryId === categoryId)
        .map((item) => ({
          question: item.question,
          answer: item.answer,
        }));
  
      updatedItems.splice(itemIndex, 1); // Remove the deleted item
  
      update(ref(db, `knowledgeBase/${categoryId}`), {
        items: updatedItems,
      })
        .then(() => {
          console.log("Item deleted successfully");
        })
        .catch((error) => {
          console.error("Error deleting item from Firebase:", error);
        });
    } else {
      console.log("Deletion canceled by user");
    }
  };

  // Handle editing of cells
  const handleEditCellChange = (params) => {
    const { id, field, value } = params;
    const updatedData = knowledgeBase.map((row) =>
      row.id === id ? { ...row, [field]: value } : row
    );
    setKnowledgeBase(updatedData);

    const { categoryId, itemIndex } = params.row;

    update(ref(db, `knowledgeBase/${categoryId}/items/${itemIndex}`), {
      [field]: value,
    });
  };

  // Handle search functionality
  useEffect(() => {
    if (searchTerm === "") {
      setFilteredKnowledgeBase(knowledgeBase);
    } else {
      const filteredData = knowledgeBase.filter(
        (item) =>
          item.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.answer.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredKnowledgeBase(filteredData);
    }
  }, [searchTerm, knowledgeBase]);

  // Handle opening and closing of the dialog
  const handleDialogOpen = () => setIsDialogOpen(true);
  const handleDialogClose = () => setIsDialogOpen(false);

  // Handle adding new record
  const handleAddRecord = () => {
    if (newCategory && newQuestion && newAnswer) {
      const categoryKey = categories.find((cat) => cat.name === newCategory);

      const newRecord = {
        question: newQuestion,
        answer: newAnswer,
      };

      const categoryRef = ref(db, `knowledgeBase/${categoryKey.name}/items`);
      push(categoryRef, newRecord); // Push the new record

      // Clear the form fields after adding
      setNewCategory("");
      setNewQuestion("");
      setNewAnswer("");
      handleDialogClose();
    }
  };

  // Define columns
  const columns = [
    {
      field: "category",
      headerName: "Category",
      flex: 0.6,
      editable: false,
    },
    {
      field: "question",
      headerName: "Question",
      flex: 2,
      editable: true,
    },
    {
      field: "answer",
      headerName: "Answer",
      flex: 3,
      editable: true,
    },
    {
      field: "actions",
      headerName: "Actions",
      flex: 0.2,
      renderCell: (params) => (
        <FontAwesomeIcon
          icon={faTrash}
          onClick={() =>
            handleDelete(params.id, params.row.categoryId, params.row.itemIndex)
          }
        />
      ),
    },
  ];

  return (
    <>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap" // Ensure it wraps on smaller screens
        sx={{ p:1,  backgroundColor: theme.palette.primary.main, mb: theme.spacing(4) }}
      >
        <Typography
          variant="h5"
          component="h2"
          gutterBottom
          color={theme.palette.success.text}
        >
          KnowledgeBase Management
        </Typography>

        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
          {/* Use SearchBar Component */}
          <Box width={500} >
          <SearchBar
            searchTerm={searchTerm}
            onChange={setSearchTerm}
            placeholderText="Search for an existing article"
            forceExpanded={true} // Force the search bar to stay expanded
          />
          </Box>
          <IconButton onClick={handleDialogOpen} color="primary">
            <FontAwesomeIcon icon={faPlus} />
          </IconButton>
        </Box>
      </Box>

      {/* DataGrid */}
      <Box
        sx={{
          padding: theme.spacing(1),
          backgroundColor: theme.palette.success.main,
        }}
      >
        <DataGrid
          rows={filteredKnowledgeBase}
          columns={columns}
          pageSize={5}
          rowsPerPageOptions={[5, 10, 100]}
          checkboxSelection
          disableSelectionOnClick
          onCellEditCommit={handleEditCellChange}
          autoHeight
        />
      </Box>

      {/* Dialog for Adding New Article */}
      <Dialog open={isDialogOpen} onClose={handleDialogClose}>
        <DialogTitle>Add New Article</DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ marginBottom: theme.spacing(2) }}>
            <InputLabel>Category</InputLabel>
            <Select
              value={newCategory}
              onChange={(e) => setNewCategory(e.target.value)}
            >
              {categories.map((cat) => (
                <MenuItem key={cat.name} value={cat.name}>
                  {cat.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="Question"
            variant="outlined"
            fullWidth
            value={newQuestion}
            onChange={(e) => setNewQuestion(e.target.value)}
            sx={{ marginBottom: theme.spacing(2) }}
          />

          <TextField
            label="Answer"
            variant="outlined"
            fullWidth
            value={newAnswer}
            onChange={(e) => setNewAnswer(e.target.value)}
            sx={{ marginBottom: theme.spacing(2) }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Cancel</Button>
          <Button onClick={handleAddRecord} variant="contained" color="primary">
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default KnowledgeBaseSettings;
